'use client'

import React, { forwardRef } from 'react'

import { buttonStyles, iconStyles } from './styles'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { Link, LinkType } from '@sidecar-components/atoms/Link'
import { Sentiment } from '@sidecar-components/types'
import { Tooltip } from '@sidecar-components/atoms/Tooltip'
import { IconButtonProps } from './IconButton.types'

/**
 * We need the forwardRef so Links can be modal/menu triggers
 *
 * @param {string} ariaControls - References the ID of the element this button
 *   controls (for screen readers)
 * @param {boolean} busy - Whether the button is in the busy state or not
 * @param {string} className - Any additional classes to add to the icon button
 * @param {string} testId - Adds a data-testid param to the button for Heap
 *   tracking and automated testing.
 * @param {boolean} disabled - Whether the user can interact with the button or
 *   not
 * @param {string} id - Unique identifier
 * @param {string} icon - Which icon to show
 * @param {string} iconClass - Any additional classes to add to the icon
 * @param {string} label - What should be shown in a tooltip
 * @param {boolean} hideTooltip - Don't convert the label into a tooltip
 * @param {function} onClick - Function
 * @param {string} path - URL the button should send you to
 * @param {LinkType} pathType - Whether the path is internal, external, or
 *   contact
 * @param {Sentiment} sentiment - What the icon is for
 * @param {IconSize} size - Size of the icon
 * @param {string} target - Whether the link opens in a new window. Only applies
 *   when a path is set
 * @param {React.ButtonHTMLAttributes<HTMLButtonElement>['type']} type - Either
 *   button/reset/submit
 * @returns {React.ReactNode} - An icon button component
 */
export const IconButton = forwardRef(
  (
    {
      ariaControls,
      busy,
      className = '',
      testId,
      disabled,
      id = '',
      icon,
      iconClass,
      label,
      hideTooltip,
      onClick,
      path,
      pathType = LinkType.INTERNAL,
      sentiment = Sentiment.DEFAULT,
      size = IconSize.MD,
      // Destructured so it never lands on the <button> branch below, where
      // target isn't a valid attribute
      target,
      tabIndex,
      type,
      // Pulled out on its own: an ancestor Radix asChild Trigger (Toggletip,
      // Tooltip with addButtonWrapper={false}) merges its own often-empty
      // data-testid into rest. Resolving it explicitly below (testId wins,
      // falling back to whatever rest carried) fixes that clobbering without
      // reordering the rest of the spread - which would otherwise flip
      // precedence for every other raw DOM attribute a caller passes
      // (aria-label, aria-controls, etc.) ahead of our own explicit ones.
      'data-testid': restTestId,
      ...rest
    }: IconButtonProps,
    forwardedRef: any,
  ) => {
    const iconContent = (
      <Icon
        name={busy ? 'loading' : icon}
        size={size}
        className={`${iconStyles({
          busy: busy,
          disabled: disabled,
          sentiment,
        })} ${iconClass}`}
      />
    )

    const buttonContent = path ? (
      <Link
        path={path}
        type={pathType}
        className={`${buttonStyles({
          disabled: disabled || busy,
          sentiment,
        })} ${className}`}
        testId={testId ?? restTestId}
        disabled={disabled || busy}
        aria-controls={ariaControls}
        aria-label={label}
        target={target}
        tooltip={label}
        // removeStyles prevents the Link styles from messing up the Button styles
        removeStyles={true}
        ref={forwardedRef}
        {...rest}
      >
        {iconContent}
      </Link>
    ) : (
      <button
        onClick={onClick}
        className={`${buttonStyles({
          disabled: disabled || busy,
          sentiment,
        })} ${className}`}
        data-testid={testId ?? restTestId}
        disabled={disabled || busy}
        id={id}
        aria-controls={ariaControls}
        aria-label={label}
        ref={forwardedRef}
        tabIndex={tabIndex}
        type={type}
        {...rest}
      >
        {iconContent}
      </button>
    )

    if (!hideTooltip) {
      // By default, the IconButton should be wrapped in a tooltip
      // so its label appears on hover and focus.
      return (
        <Tooltip
          addButtonWrapper={false}
          children={buttonContent}
          content={label}
          // add the testId here too because
          // addButtonWrapper overrides the button's testId
          testId={testId}
        />
      )
    } else {
      // On rare occations, we can turn off the tooltip
      // Example: X button in the modal header
      return buttonContent
    }
  },
)
